import { useEffect, useRef, useState } from "react";
import { useT, useI18n } from "@/lib/i18n";

const FK_BASE = "https://fritzkammag.ch/wp-content/uploads/2018/01";

/* Drei Verfahren mit dem gleichen Foto links und rechts.
   Auf der "Vorher"-Seite simulieren wir per CSS-Filter den unbearbeiteten
   Zustand (rostig / matt / roh). Sobald Marco echte Bauteil-Vor/Nach-Fotos
   schickt, ersetzen wir before/after einfach durch zwei separate URLs und
   lassen das filter-Feld leer. */
const PAIRS = [
  {
    id: "verzinken",
    image: `${FK_BASE}/Verzinken-1.jpg`,
    // Rostig/oxidiert: warme Sepia + Sättigung kippt Silber zu Rost-Orange
    beforeFilter: "sepia(0.85) saturate(2.6) hue-rotate(-22deg) brightness(0.55) contrast(1.2)",
    titleKey: "verzinken",
  },
  {
    id: "elektropolieren",
    image: `${FK_BASE}/cns-Elektropolieren-1.png`,
    // Matt + entsättigt + leicht abgedunkelt — wie unbearbeiteter Edelstahl nach dem Schweissen
    beforeFilter: "grayscale(0.85) brightness(0.55) contrast(0.9) blur(0.4px)",
    titleKey: "elektropolieren",
  },
  {
    id: "bruenieren",
    image: `${FK_BASE}/Br%C3%BCnieren-2.png`,
    // Vorher: heller, ungebräunter Stahl — invertieren der Schwarz-Mattigkeit
    beforeFilter: "brightness(2.4) contrast(0.9) saturate(0.4) sepia(0.2)",
    titleKey: "bruenieren",
  },
];

const COPY = {
  de: {
    overline: "— Vorher / Nachher",
    title_1: "Sehen, was Galvanik",
    title_2: "wirklich verändert.",
    intro: "Drei reale Veredelungen — und der direkte Beweis. Ziehen Sie den Schieber zur Seite und sehen Sie selbst, was unsere Bäder aus rohem Metall machen.",
    before_label: "ROH",
    after_label: "VEREDELT",
    drag_hint: "Schieber bewegen",
    demo_note: "Demo-Visualisierung · echte Vor/Nach-Bauteilfotos folgen",
    titles: {
      elektropolieren: "Edelstahl · vor und nach CNS-Elektropolieren",
      verzinken: "Stahl · vor und nach Verzinken",
      bruenieren: "Werkzeugstahl · vor und nach Brünieren",
    },
    rows: {
      elektropolieren: ["Ra ≤ 0.4 µm", "Reinraumtauglich", "Spiegelnde Oberfläche"],
      verzinken: ["8–12 µm Schichtdicke", "240–720 h Salzsprühnebel", "Blau passiviert"],
      bruenieren: ["1–2 µm Konversion", "Mattschwarz", "Maßhaltig"],
    },
  },
  fr: {
    overline: "— Avant / Après",
    title_1: "Voyez ce que la galvanoplastie",
    title_2: "change vraiment.",
    intro: "Trois traitements réels — et la preuve directe. Faites glisser le curseur et constatez par vous-même ce que nos bains transforment du métal brut.",
    before_label: "BRUT",
    after_label: "TRAITÉ",
    drag_hint: "Faire glisser",
    demo_note: "Visualisation démo · photos de pièces réelles à suivre",
    titles: {
      elektropolieren: "Inox · avant et après polissage électrolytique",
      verzinken: "Acier · avant et après zingage",
      bruenieren: "Acier outil · avant et après brunissage",
    },
    rows: {
      elektropolieren: ["Ra ≤ 0,4 µm", "Compatible salle blanche", "Surface miroir"],
      verzinken: ["8–12 µm d'épaisseur", "240–720 h brouillard salin", "Passivation bleue"],
      bruenieren: ["Conversion 1–2 µm", "Noir mat", "Cotes stables"],
    },
  },
  en: {
    overline: "— Before / After",
    title_1: "See what electroplating",
    title_2: "really changes.",
    intro: "Three real finishes — and the direct proof. Drag the slider and see for yourself what our baths do to raw metal.",
    before_label: "RAW",
    after_label: "FINISHED",
    drag_hint: "Drag the slider",
    demo_note: "Demo visualisation · real before/after photos to follow",
    titles: {
      elektropolieren: "Stainless · before and after electropolishing",
      verzinken: "Steel · before and after zinc plating",
      bruenieren: "Tool steel · before and after black oxide",
    },
    rows: {
      elektropolieren: ["Ra ≤ 0.4 µm", "Cleanroom-grade", "Mirror finish"],
      verzinken: ["8–12 µm thickness", "240–720 h salt spray", "Blue-passivated"],
      bruenieren: ["1–2 µm conversion", "Matt black", "Dimensionally stable"],
    },
  },
  it: {
    overline: "— Prima / Dopo",
    title_1: "Vedete cosa la galvanica",
    title_2: "cambia davvero.",
    intro: "Tre trattamenti reali — e la prova diretta. Trascinate il cursore e constatate voi stessi cosa fanno i nostri bagni al metallo grezzo.",
    before_label: "GREZZO",
    after_label: "TRATTATO",
    drag_hint: "Sposta il cursore",
    demo_note: "Visualizzazione demo · le foto reali prima/dopo seguiranno",
    titles: {
      elektropolieren: "Inox · prima e dopo l'elettrolucidatura",
      verzinken: "Acciaio · prima e dopo la zincatura",
      bruenieren: "Acciaio per utensili · prima e dopo la brunitura",
    },
    rows: {
      elektropolieren: ["Ra ≤ 0,4 µm", "Idoneo camera bianca", "Superficie a specchio"],
      verzinken: ["8–12 µm di spessore", "240–720 h nebbia salina", "Passivato blu"],
      bruenieren: ["Conversione 1–2 µm", "Nero opaco", "Dimensioni stabili"],
    },
  },
};

const Slider = ({ pair, copy }) => {
  const ref = useRef(null);
  const [pos, setPos] = useState(50);
  const [dragging, setDragging] = useState(false);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const handle = () => ref.current && setWidth(ref.current.getBoundingClientRect().width);
    handle();
    window.addEventListener("resize", handle);
    return () => window.removeEventListener("resize", handle);
  }, []);

  const move = (clientX) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const next = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.max(2, Math.min(98, next)));
  };

  useEffect(() => {
    if (!dragging) return;
    const onMove = (e) => move(e.touches ? e.touches[0].clientX : e.clientX);
    const onUp = () => setDragging(false);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onMove);
    window.addEventListener("touchend", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
    };
  }, [dragging]);

  return (
    <div
      ref={ref}
      data-testid={`ba-slider-${pair.id}`}
      className="relative w-full aspect-[16/9] overflow-hidden border border-white/10 select-none bg-black"
      onMouseDown={(e) => { setDragging(true); move(e.clientX); }}
      onTouchStart={(e) => { setDragging(true); move(e.touches[0].clientX); }}
    >
      {/* AFTER — full background */}
      <img
        src={pair.image}
        alt={`${copy.titles[pair.titleKey]} — ${copy.after_label}`}
        className="absolute inset-0 w-full h-full object-cover"
        draggable={false}
      />
      {/* BEFORE — clipped overlay with simulated raw filter */}
      <div className="absolute inset-y-0 left-0 overflow-hidden" style={{ width: `${pos}%` }}>
        <img
          src={pair.image}
          alt={`${copy.titles[pair.titleKey]} — ${copy.before_label}`}
          className="absolute inset-0 h-full object-cover"
          style={{ width: `${width || 0}px`, filter: pair.beforeFilter }}
          draggable={false}
        />
        {/* subtle grain to make raw side feel rougher */}
        <div
          className="absolute inset-0 opacity-30 pointer-events-none mix-blend-overlay"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='240' height='240'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.55'/></svg>\")",
          }}
        />
      </div>

      <span className="absolute top-4 left-4 px-3 py-1 bg-black/70 backdrop-blur-sm text-[10px] uppercase tracking-[0.22em] text-white border border-white/20 z-10">
        {copy.before_label}
      </span>
      <span className="absolute top-4 right-4 px-3 py-1 bg-white text-[10px] uppercase tracking-[0.22em] text-black z-10">
        {copy.after_label}
      </span>

      <div className="absolute top-0 bottom-0 w-px bg-white pointer-events-none z-20" style={{ left: `${pos}%` }} />
      <div
        role="slider"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(pos)}
        className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-12 h-12 rounded-full bg-white border-2 border-white shadow-2xl flex items-center justify-center cursor-ew-resize z-30"
        style={{ left: `${pos}%` }}
      >
        <svg viewBox="0 0 24 24" className="w-5 h-5 text-black" fill="none">
          <path d="M9 6 L4 12 L9 18 M15 6 L20 12 L15 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
    </div>
  );
};

export const BeforeAfter = () => {
  const t = useT();
  const { lang } = useI18n();
  const c = COPY[lang] || COPY.de;
  const [active, setActive] = useState(0);
  const pair = PAIRS[active];

  return (
    <section
      id="vorher-nachher"
      data-testid="ba-section"
      className="relative bg-[#0E0E10] py-24 md:py-32 border-t border-white/5"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-12">
          <div className="lg:col-span-7">
            <div className="overline reveal mb-5">{c.overline}</div>
            <h2 className="reveal reveal-delay-1 font-display text-4xl sm:text-5xl lg:text-[60px] font-semibold tracking-tight leading-[0.95] text-white">
              {c.title_1}
              <br />
              <span className="text-zinc-500">{c.title_2}</span>
            </h2>
          </div>
          <div className="lg:col-span-5 flex items-end">
            <p className="reveal reveal-delay-2 text-zinc-400 text-base md:text-lg leading-relaxed max-w-md">
              {c.intro}
            </p>
          </div>
        </div>

        <div className="reveal flex flex-wrap gap-2 mb-6" role="tablist">
          {PAIRS.map((p, i) => {
            const isActive = i === active;
            return (
              <button
                key={p.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                data-testid={`ba-tab-${p.id}`}
                onClick={() => setActive(i)}
                className={`px-4 py-2 text-[12px] uppercase tracking-[0.18em] border transition-all duration-300 ${
                  isActive
                    ? "bg-white text-black border-white"
                    : "border-white/15 text-zinc-400 hover:border-white/40 hover:text-white"
                }`}
              >
                {c.titles[p.titleKey].split("·")[0].trim()}
              </button>
            );
          })}
        </div>

        <div className="reveal">
          <Slider pair={pair} copy={c} />
        </div>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-12 gap-6 items-start">
          <div className="md:col-span-7">
            <div className="font-display text-xl md:text-2xl text-white tracking-tight mb-2">
              {c.titles[pair.titleKey]}
            </div>
            <div className="text-[12px] text-zinc-500 inline-flex items-center gap-2">
              <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14M5 12l4-4M5 12l4 4M19 12l-4-4M19 12l-4 4" strokeLinecap="round" strokeLinejoin="round" /></svg>
              {c.drag_hint}
            </div>
          </div>
          <ul className="md:col-span-5 grid grid-cols-3 gap-px bg-white/10 border border-white/10">
            {c.rows[pair.titleKey].map((r) => (
              <li key={r} className="bg-[#0E0E10] p-3 md:p-4 text-[11px] uppercase tracking-[0.14em] text-zinc-200 text-center">
                {r}
              </li>
            ))}
          </ul>
        </div>

        <div className="mt-8 text-[11px] text-zinc-500 italic">{c.demo_note}</div>
      </div>
    </section>
  );
};

export default BeforeAfter;
